/**
 * Turns a forward-looking time phrase in a question ("this week", "next month",
 * "in the next 14 days", "in October") into the days a question about what is
 * *coming up* covers.
 *
 * {@link ./periodParser} reads time backwards: there "this month" is the month
 * so far, which is right for "what did I earn this month" and wrong for "which
 * bookings end this month", where the days still to come are the whole point.
 * The two readings are kept apart so neither has to special-case the other.
 *
 * Like the period parser, every window carries a {@link UpcomingWindow.label}
 * that the answer is expected to repeat, so a phrase that was not recognised
 * still reads back as the default window it was answered for.
 */

import moment from 'moment';
import { containsFuzzyPhrase, tokenize } from './fuzzyMatch';

export interface UpcomingWindow {
  /**
   * How the window is named back to the user, phrased to follow a verb —
   * "3 bookings end {label}" — so answers need no special cases.
   */
  label: string;
  /** Inclusive first day, `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive last day, `YYYY-MM-DD`. */
  endDate: string;
  /** False when nothing in the message named a window and the default was used. */
  explicit: boolean;
}

/** Days covered when the question names no window ("which bookings end soon"). */
export const DEFAULT_UPCOMING_DAYS = 30;

const ISO_DAY = 'YYYY-MM-DD';

interface NamedWindow {
  label: string;
  phrases: string[];
  range: (today: moment.Moment) => { start: moment.Moment; end: moment.Moment };
}

/** From today to the end of the current `unit`: what is left of it. */
const restOf = (unit: moment.unitOfTime.StartOf) => (today: moment.Moment) => ({
  start: today.clone(),
  end: today.clone().endOf(unit),
});

/** The whole of the following `unit`. */
const following = (unit: moment.unitOfTime.StartOf & moment.unitOfTime.DurationConstructor) =>
  (today: moment.Moment) => ({
    start: today.clone().add(1, unit).startOf(unit),
    end: today.clone().add(1, unit).endOf(unit),
  });

const NAMED_WINDOWS: NamedWindow[] = [
  {
    label: 'today',
    phrases: ['today', 'end of the day', 'end of today'],
    range: restOf('day'),
  },
  {
    label: 'tomorrow',
    phrases: ['tomorrow'],
    range: (today) => ({
      start: today.clone().add(1, 'day'),
      end: today.clone().add(1, 'day').endOf('day'),
    }),
  },
  {
    label: 'this week',
    phrases: ['this week', 'end of the week', 'end of this week', 'end of week'],
    range: restOf('week'),
  },
  { label: 'next week', phrases: ['next week', 'following week'], range: following('week') },
  {
    label: 'this month',
    phrases: ['this month', 'end of the month', 'end of this month', 'end of month', 'month end'],
    range: restOf('month'),
  },
  { label: 'next month', phrases: ['next month', 'following month'], range: following('month') },
  {
    label: 'this quarter',
    phrases: ['this quarter', 'end of the quarter', 'end of this quarter', 'quarter end'],
    range: restOf('quarter'),
  },
  { label: 'next quarter', phrases: ['next quarter'], range: following('quarter') },
  {
    label: 'this year',
    phrases: ['this year', 'end of the year', 'end of this year', 'end of year', 'year end'],
    range: restOf('year'),
  },
  { label: 'next year', phrases: ['next year'], range: following('year') },
];

const NUMBER_WORDS: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};

/** "next 14 days", "within 2 weeks", "in the next three months", "over the coming 60 days". */
const RELATIVE = new RegExp(
  '\\b(?:next|coming|within|in|over|during)\\s+(?:the\\s+)?(?:next\\s+|coming\\s+)?' +
    `(\\d{1,3}|${Object.keys(NUMBER_WORDS).join('|')})\\s+(days?|weeks?|months?)\\b`,
  'i',
);

/** "within the next month", "over the coming week": one of the unit, counted from today. */
const WITHIN_NEXT = /\b(?:within|in|over|during)\s+the\s+(?:next|coming)\s+(day|week|month)\b/i;

const MONTH_NAMES = moment.months().map((name) => name.toLowerCase());

/**
 * A month named on its own, full or abbreviated. "May" only counts after a
 * preposition — "which bookings may end soon" is not about May.
 */
const MONTH = new RegExp(
  '\\b(?:(in|during|by|of|through|thru|before)\\s+)?' +
    '(january|february|march|april|may|june|july|august|september|october|november|december|' +
    'jan|feb|apr|jun|jul|aug|sept|sep|oct|nov|dec)\\b',
  'gi',
);

const monthIndex = (word: string): number =>
  MONTH_NAMES.findIndex((name) => name.startsWith(word.toLowerCase().slice(0, 3)));

const toWindow = (
  label: string,
  start: moment.Moment,
  end: moment.Moment,
  explicit: boolean,
): UpcomingWindow => ({
  label,
  startDate: start.format(ISO_DAY),
  endDate: end.format(ISO_DAY),
  explicit,
});

type RelativeUnit = 'day' | 'week' | 'month';

const toUnit = (word: string): RelativeUnit => word.toLowerCase().replace(/s$/, '') as RelativeUnit;

const relativeCount = (message: string): { count: number; unit: RelativeUnit } | null => {
  const counted = message.match(RELATIVE);
  if (counted) {
    return { count: NUMBER_WORDS[counted[1].toLowerCase()] ?? Number(counted[1]), unit: toUnit(counted[2]) };
  }
  const single = message.match(WITHIN_NEXT);
  return single ? { count: 1, unit: toUnit(single[1]) } : null;
};

const relativeWindow = (message: string, today: moment.Moment): UpcomingWindow | null => {
  const relative = relativeCount(message);
  if (!relative) return null;
  const { count, unit } = relative;
  if (!Number.isFinite(count) || count <= 0) return null;
  const label = `in the next ${count === 1 ? unit : `${count} ${unit}s`}`;
  return toWindow(label, today, today.clone().add(count, unit), true);
};

/**
 * The next time the named month comes round: this year's if it has not ended
 * yet, otherwise next year's — nobody asks which bookings end in a month that
 * is already over.
 */
const monthWindow = (message: string, today: moment.Moment): UpcomingWindow | null => {
  // Every match, not just the first: in "may I see which bookings end in August" the
  // first is the polite verb, and August is the month being asked about.
  const month = Array.from(message.matchAll(MONTH)).find(
    ([, preposition, word]) => word.toLowerCase() !== 'may' || Boolean(preposition),
  );
  if (!month) return null;
  const index = monthIndex(month[2]);
  if (index < 0) return null;

  let start = today.clone().month(index).startOf('month');
  if (start.clone().endOf('month').isBefore(today)) start = start.add(1, 'year');
  const end = start.clone().endOf('month');
  const name = MONTH_NAMES[index].charAt(0).toUpperCase() + MONTH_NAMES[index].slice(1);
  const label = `in ${name}${start.year() === today.year() ? '' : ` ${start.year()}`}`;
  return toWindow(label, moment.max(start, today), end, true);
};

const namedWindow = (message: string, today: moment.Moment): UpcomingWindow | null => {
  const tokens = tokenize(message);
  let best: NamedWindow | null = null;
  let bestLength = 0;

  for (const window of NAMED_WINDOWS) {
    for (const phrase of window.phrases) {
      if (phrase.length <= bestLength) continue;
      if (!containsFuzzyPhrase(tokens, tokenize(phrase))) continue;
      best = window;
      bestLength = phrase.length;
    }
  }

  if (!best) return null;
  const { start, end } = best.range(today);
  return toWindow(best.label, start, end, true);
};

/**
 * The window of days still to come that a message asks about, or the next
 * {@link DEFAULT_UPCOMING_DAYS} days when it names none.
 *
 * An explicit count wins over a named window ("in the next 2 weeks" is not
 * "next week"), and a named month over the named windows, since "the end of
 * October" means October rather than the end of the current month.
 */
export const resolveUpcomingWindow = (message: string): UpcomingWindow => {
  const today = moment().startOf('day');
  return (
    relativeWindow(message, today) ??
    monthWindow(message, today) ??
    namedWindow(message, today) ??
    toWindow(
      `in the next ${DEFAULT_UPCOMING_DAYS} days`,
      today,
      today.clone().add(DEFAULT_UPCOMING_DAYS, 'days'),
      false,
    )
  );
};
